import ApexCharts from 'apexcharts';
import {
  AccessDataProtection,
  GetAccessDataProtectionResponse,
  Status,
} from '@nirvana/shared/dtos/types/widgets/common';
import { DisplayMode, TemplateState } from '../../types/common/types';
import { BackendResponseWithMeta, DASHBOARD_METADATA_KEY } from '../../types/widget/types';
import { BaseThreatWidgetView } from '../../widget/BaseThreatWidgetView';
import { Target } from '@nirvana/shared/dtos/types/dashboards/common';
import {
  AccessDataProtectionNavigationHelper,
  CommonThreatsNavigationHelper,
  NavigationItem,
} from '@nirvana/shared/navigation';

export class AccessDataProtectionView extends BaseThreatWidgetView {
  public latestData: AccessDataProtection;
  public shadow: any;
  protected translations: any;
  private readonly $chartContainer: HTMLElement;
  private $chart: any;
  private $style: any;
  private chartColors: Array<string>;
  private errorColor: string;
  private options: any;
  private basicOptions: any;
  private categories: any;
  private columnsData: any;
  private chartSeries: any;

  getDomElements() {
    super.getDomElements();
    this.dom.$chartContainer = this.shadow.querySelector('#accessDataProtectionChart');
    this.dom.$widgetTitle = this.shadow.querySelector('.widget-title');
    this.dom.$widgetTitleString = this.shadow.querySelector('#accessDataProtectionTitle');
    this.$style = getComputedStyle(document.body);
    this.getTranslations();
    this.getChartColors();
    this.setTranslatedStrings();
    this.setBasicOptions();
    this.setErrorTooltip();
  }

  getTranslations() {
    this.translations = this.stringsTranslator.getAccessDataProtectionWidgetStrings();
  }

  private getChartColors() {
    const color1 = this.$style.getPropertyValue('--orange-alert-chart-100');
    const color2 = this.$style.getPropertyValue('--orange-alert-chart-100');
    const color3 = this.$style.getPropertyValue('--orange-alert-chart-100');
    const color4 = this.$style.getPropertyValue('--orange-alert-chart-100');

    this.chartColors = [color1, color2, color3, color4];

    for (let i = 0; i < this.chartColors.length; i++) {
      this.chartColors[i] = this.chartColors[i].trimLeft();
    }

    this.errorColor = this.$style.getPropertyValue('--grey-layout-203-dark-theme-disabled-text').trimLeft();
  }

  buildSuccessView(response: BackendResponseWithMeta<GetAccessDataProtectionResponse>) {
    this.resetTemplateState(this.$wrapper);

    const result = response.widgetResult;

    if (result.status === Status.EMPTY_STATE) {
      this.setTemplateState(this.$wrapper, TemplateState.EMPTY);
      this.setAnimation(response[DASHBOARD_METADATA_KEY].displayMode);
      this.buildEmptyTemplate();
      return;
    }

    if (result.status === Status.ACTIVE && result.data) {
      this.latestData = result.data;
      this.setDataLabels(response[DASHBOARD_METADATA_KEY].displayMode);
      this.setAnimation(response[DASHBOARD_METADATA_KEY].displayMode);
      this.setDataChart(this.latestData);

      this.dashboardMetaData = {
        targets: response[DASHBOARD_METADATA_KEY].targets,
        timeRange: response[DASHBOARD_METADATA_KEY].reportingPeriod.timeRange,
        canDrillDown: response[DASHBOARD_METADATA_KEY].canDrillDown,
      };

      this.addDrillDownHandlers(
        this.dashboardMetaData.targets,
        this.dashboardMetaData.timeRange,
        this.dashboardMetaData.canDrillDown
      );
      return;
    }

    this.buildErrorView();
  }

  setTranslatedStrings() {
    const { title, columnNames } = this.translations;
    this.dom.$widgetTitleString.innerHTML = title;

    /**
     * If the category is an array of strings, i.e: ['Device', 'Control'] it will be displayed on 2 lines
     * ['lorem', 'ipsum', 'dolor'] will be displayed on 3 lines etc.
     * If the category is a string, it will be displayed normally, on a single line.
     *
     * See:  https://apexcharts.com/javascript-chart-demos/column-charts/distributed/
     *
     * This line of code splits the name of categories to show them as desired in widget design
     */
    this.categories = columnNames.map(name => (name.includes(' ') ? name.split(' ') : name));
  }

  private setDataChart(data: AccessDataProtection) {
    this.columnsData = [data.deviceControl, data.connectionsBlocked, data.applications, data.websitesBlocked];
    this.chartSeries = [
      {
        data: this.columnsData,
      },
    ];

    this.options = {
      series: this.chartSeries,
      colors: this.chartColors,
      tooltip: {
        followCursor: true,
        shared: true,
        intersect: false,
        enabled: true,
        custom({ series, seriesIndex, dataPointIndex, w }) {
          return `
              <div class="tooltip">
                <div class="tooltip--label">${w.globals.labels[dataPointIndex].join(' ')}</div>
                <div class="tooltip--value">${series[seriesIndex][dataPointIndex]}</div>
              </div>
          `;
        },
      },
      states: {
        active: {
          filter: {
            type: 'none',
          },
        },
        hover: {
          filter: {
            type: 'lighten',
            value: 0.05,
          },
        },
      },
      ...this.basicOptions,
    };

    this.buildChart(this.chartSeries, this.options);
  }

  buildErrorView() {
    this.resetTemplateState(this.$wrapper);
    this.setTemplateState(this.$wrapper, TemplateState.ERROR);
    this.buildEmptyTemplate();
  }

  private buildEmptyTemplate() {
    this.columnsData = [100, 100, 100, 100];
    this.chartSeries = [
      {
        data: this.columnsData,
      },
    ];

    this.options = {
      series: this.chartSeries,
      colors: [this.errorColor, this.errorColor, this.errorColor, this.errorColor],
      tooltip: {
        enabled: false,
      },
      states: {
        hover: {
          filter: {
            type: 'none',
          },
        },
      },
      ...this.basicOptions,
    };

    this.buildChart(this.chartSeries, this.options);
  }

  private buildChart(chartSeries, options) {
    if (!this.$chart) {
      this.$chart = new ApexCharts(this.dom.$chartContainer, this.options);
      this.$chart.render();
      return;
    } else {
      this.$chart.updateSeries(chartSeries, true);
      this.$chart.updateOptions(options);
    }
  }

  private setBasicOptions() {
    this.basicOptions = {
      chart: {
        type: 'bar',
        toolbar: {
          show: false,
        },
        width: '100%',
        height: '194px',
        events: {
          updated: (chartContext, config) => {
            this.updateDrillDownHandlers();
          },
        },
        animations: {
          enabled: true,
        },
      },
      plotOptions: {
        bar: {
          columnWidth: '70%',
          distributed: true,
          dataLabels: {
            position: 'top', // top, center, bottom
          },
        },
      },
      xaxis: {
        position: 'bottom',
        categories: this.categories,
        axisTicks: {
          show: false,
        },
        labels: {
          hideOverlappingLabels: false,
          rotate: -40,
          style: {
            colors: this.$style.getPropertyValue('--body-text-arrows-400').trimLeft(),
          },
        },
      },
      yaxis: {
        crosshairs: {
          show: false,
        },
        tickAmount: 4,
        min: 0,
        max(max) {
          return Math.ceil(max / 10) * 10; // Transforms 1 to 10, 11 to 20, 351 into 360 etc.
        },
        floating: true,
        labels: {
          minWidth: 50,
          style: {
            colors: this.$style.getPropertyValue('--body-text-arrows-400').trimLeft(),
          },
        },
      },
      grid: {
        borderColor: this.$style.getPropertyValue('--chart-101-pressed-dividers').trimLeft(),
        xaxis: {
          lines: {
            show: false,
          },
        },
      },
      fill: {
        opacity: 1,
      },
      legend: {
        show: false,
      },
      basicOptions: {
        dataLabels: false,
      },
    };
  }

  protected getNavigationItemForTitle(targets: Array<Target>, timeRange: number): NavigationItem {
    const isPartner = Application.Main.company.isPartner();
    const filters = AccessDataProtectionNavigationHelper.getFiltersForNavigationItem({
      itemKey: 'title',
      timeRange: timeRange,
      targets: targets,
      isPartner: isPartner,
    });
    const displayedColumns = AccessDataProtectionNavigationHelper.getDisplayedColumns();

    return CommonThreatsNavigationHelper.buildNavigationItem([this.dom.$widgetTitle], filters, displayedColumns);
  }

  protected getNavigationItemsForWidgetBody(targets: Array<Target>, timeRange: number): Array<NavigationItem> {
    const xAxisLabels = this.getXAxisLabels();

    const navigationItems: Array<NavigationItem> = [];

    xAxisLabels.forEach((labelElement: HTMLElement, elementIndex: number) => {
      navigationItems.push(this.buildNavigationItemForAxisLabel(labelElement, elementIndex, targets, timeRange));
    });

    return navigationItems;
  }

  private buildNavigationItemForAxisLabel(
    labelElement: HTMLElement,
    elementIndex: number,
    targets: Array<Target>,
    timeRange: number
  ) {
    const itemKey = Object.keys(this.latestData)[elementIndex];
    const isPartner = Application.Main.company.isPartner();
    const displayedColumns = AccessDataProtectionNavigationHelper.getDisplayedColumns();
    const filters = AccessDataProtectionNavigationHelper.getFiltersForNavigationItem({
      itemKey: itemKey,
      timeRange: timeRange,
      targets: targets,
      isPartner: isPartner,
    });

    return CommonThreatsNavigationHelper.buildNavigationItem([labelElement], filters, displayedColumns);
  }

  private setDataLabels(displayMode: DisplayMode) {
    const enabled = displayMode !== DisplayMode.DASHBOARD;

    this.basicOptions.dataLabels = {
      enabled: enabled,
      textAnchor: 'middle',
      offsetY: -13,
      style: {
        fontSize: '12px',
        fontFamily: this.$style.getPropertyValue('--font-family'),
        fontWeight: 'bold',
        colors: ['#333333'],
      },
    };
  }

  private setAnimation(displayMode: DisplayMode) {
    const animationsEnable = displayMode === DisplayMode.DASHBOARD;

    this.basicOptions.chart.animations = {
      enabled: animationsEnable,
    };
  }

  private getXAxisLabels(): NodeList {
    return this.$chart.el.querySelectorAll('.apexcharts-xaxis .apexcharts-xaxis-label');
  }

  protected updateDrillDownHandlers() {
    if (!this.dom.$widgetTitle || !this.getXAxisLabels().length) {
      return;
    }

    super.updateDrillDownHandlers();
  }
}
